import React, { useState, useMemo } from 'react';
import { Loader2, AlertCircle, History, ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useEntityAuditLogs } from '@/hooks/useEntityAuditLogs';
import { getActionConfig } from '@/config/audit-logs.config';
import { CreationTimelineItem } from './CreationTimelineItem';
import { AuditDetailsList, AuditTechnicalBlock, AuditTechnicalData } from './AuditLogDetails';
import { technicalData } from './auditLogTechnicalData';
import type { AuditLog } from '@/features/external-users/types/audit-log.types';

type EntityType = 'internal-user' | 'external-user' | 'society' | 'warranty' | 'contract' | 'general-conditions';

interface GroupedLog {
  type: 'creation-group';
  mainLog: AuditLog;
  detailLogs: AuditLog[];
}

interface SingleLog {
  type: 'single';
  log: AuditLog;
}

type DisplayLog = GroupedLog | SingleLog;

interface AuditLogsCardProps {
  entityType: EntityType;
  entityId: string;
  title?: string;
  limit?: number;
  includeCreationDetails?: boolean;
  shouldGroupCreationLogs?: boolean;
  renderCustomDetail?: (log: AuditLog) => React.ReactNode;
}

interface AuditLogItemProps {
  log: AuditLog;
  renderCustomDetail?: (log: AuditLog) => React.ReactNode;
}

const AuditLogItem: React.FC<AuditLogItemProps> = ({ log, renderCustomDetail }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = getActionConfig(log.action);
  const IconComponent = config.icon;

  /*
    Ce que l'API a mis en forme, et rien d'autre : les données brutes ne sont
    montrées que dans le bloc technique. Une API qui ne renvoie pas encore les
    détails (déploiement du front en premier) n'affiche que ce bloc, jamais les
    clés brutes.
  */
  const details = log.details ?? [];
  const technique = useMemo(() => technicalData(log), [log]);
  const customContent = renderCustomDetail?.(log);
  const hasDetails = !!(log.displayedSummary || details.length > 0 || customContent || technique);

  return (
    <div className="hover:tw-bg-gray-50 tw-transition-colors">
      <div className="tw-px-4 tw-py-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
            <div className="tw-flex-shrink-0">
              <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center ${config.colorClass}`}>
                <IconComponent className="tw-w-4 tw-h-4" />
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-wrap">
              {/*
                Libellé servi par l'API en priorité : son catalogue est la
                source unique et couvre toutes les actions. La table locale ne
                vaut plus que pour l'icône et la couleur, et comme repli.
              */}
              <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
                {log.actionLabel || config.label}
              </span>
              {/*
                Trois origines, trois mentions. « Système » quand personne n'a
                agi — un prélèvement que la banque exécute, un abonnement que
                Stripe clôt. « Par le bénéficiaire » quand le client a fait un
                geste : c'est l'information qu'on vient chercher sur une
                signature, et un simple « Par X » la ferait passer pour un
                collègue.
              */}
              {log.userType === 'SYSTEM' ? (
                <span className="tw-text-xs tw-text-gray-500">- Système</span>
              ) : (
                (log.userName || log.userEmail) && (
                  <span className="tw-text-xs tw-text-gray-500">
                    {log.userType === 'EXTERNAL' ? '- Par le bénéficiaire : ' : '- Par '}
                    {log.userName || log.userEmail}
                  </span>
                )
              )}
              {log.status && (
                <span className={`tw-text-xs tw-font-medium ${log.status === 'ERROR' ? 'tw-text-red-600' : 'tw-text-green-600'}`}>
                  - {log.status === 'ERROR' ? 'Erreur' : 'Succès'}
                </span>
              )}
            </div>
          </div>

          <div className="tw-flex-shrink-0 tw-text-xs tw-text-gray-500">
            {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
          </div>
        </div>

        {hasDetails && (
          <div className="tw-mt-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-affinicar-primary hover:tw-text-affinicar-secondary tw-transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="tw-w-3.5 tw-h-3.5" />
                  <span>Masquer les détails</span>
                </>
              ) : (
                <>
                  <ChevronRight className="tw-w-3.5 tw-h-3.5" />
                  <span>Voir les détails</span>
                </>
              )}
            </button>

            {isExpanded && (
              <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-space-y-3">
                <div className="tw-text-xs">
                  <span className="tw-font-medium tw-text-gray-500">Date :</span>
                  <span className="tw-ml-1 tw-text-gray-700">
                    {format(new Date(log.createdAt), "dd/MM/yyyy 'à' HH:mm:ss", { locale: fr })}
                  </span>
                </div>

                {log.displayedSummary && (
                  <div className="tw-text-sm tw-text-gray-700 tw-italic">
                    {log.displayedSummary}
                  </div>
                )}

                <AuditDetailsList details={details} />
                {/* Affichage propre à un écran (ex : diff des droits), en plus des détails. */}
                {customContent}

                {technique && (
                  <AuditTechnicalBlock>
                    <AuditTechnicalData data={technique} />
                  </AuditTechnicalBlock>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const GenericAuditLogsCard: React.FC<AuditLogsCardProps> = ({
  entityType,
  entityId,
  title = 'Historique des modifications',
  limit = 100,
  includeCreationDetails = false,
  shouldGroupCreationLogs = false,
  renderCustomDetail,
}) => {
  const { data: logs, isLoading, isError, error } = useEntityAuditLogs(entityType, entityId, {
    limit,
    includeDetails: includeCreationDetails,
  });

  const displayLogs = useMemo<DisplayLog[]>(() => {
    if (!logs || !shouldGroupCreationLogs) {
      return logs?.map(log => ({ type: 'single' as const, log })) || [];
    }

    const grouped = new Map<string, AuditLog[]>();
    const ungrouped: AuditLog[] = [];
    const processedIds = new Set<string>();

    logs.forEach(log => {
      const isCreationLog = log.entityType?.includes('_CREATION');

      if (isCreationLog && log.correlationId) {
        const group = grouped.get(log.correlationId) ?? [];
        group.push(log);
        grouped.set(log.correlationId, group);
        processedIds.add(log.id);
      }
    });

    logs.forEach(log => {
      if (!processedIds.has(log.id)) {
        ungrouped.push(log);
      }
    });

    const result: DisplayLog[] = [];

    grouped.forEach((groupLogs) => {
      const sorted = [...groupLogs].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      const mainLog = sorted.find(l => l.action === 'INIT' || l.stepNumber === 0) || sorted[0];
      const detailLogs = sorted.filter(l => l.id !== mainLog.id);

      result.push({
        type: 'creation-group',
        mainLog,
        detailLogs,
      });
    });

    ungrouped.forEach(log => {
      result.push({
        type: 'single',
        log,
      });
    });

    return result.sort((a, b) => {
      const dateA = a.type === 'creation-group' ? a.mainLog.createdAt : a.log.createdAt;
      const dateB = b.type === 'creation-group' ? b.mainLog.createdAt : b.log.createdAt;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [logs, shouldGroupCreationLogs]);

  if (isLoading) {
    return (
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6">
        <div className="tw-flex tw-justify-center tw-items-center tw-p-6">
          <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-affinicar-primary" />
          <p className="tw-ml-3 tw-text-gray-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6">
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-p-6 tw-text-red-600">
          <AlertCircle className="tw-h-10 tw-w-10 tw-mb-3" />
          <h3 className="tw-text-lg tw-font-semibold">Erreur de chargement</h3>
          <p className="tw-text-gray-700">{(error as Error)?.message || 'Une erreur est survenue.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h2 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0 tw-flex tw-items-center tw-gap-2">
          <History className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
          {title}
        </h2>
        {displayLogs && displayLogs.length > 0 && (
          <span className="tw-text-sm tw-text-gray-500">
            {displayLogs.length} {displayLogs.length === 1 ? 'action' : 'actions'}
          </span>
        )}
      </div>

      {displayLogs && displayLogs.length > 0 ? (
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-divide-y tw-divide-gray-200">
          {displayLogs.map((item) =>
            item.type === 'creation-group' ? (
              <CreationTimelineItem
                key={`group-${item.mainLog.id}`}
                mainLog={item.mainLog}
                detailLogs={item.detailLogs}
              />
            ) : (
              <AuditLogItem
                key={item.log.id}
                log={item.log}
                renderCustomDetail={renderCustomDetail}
              />
            )
          )}
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-gray-500">
          <History className="tw-w-12 tw-h-12 tw-mb-3 tw-text-gray-300" />
          <p className="tw-text-lg tw-font-medium">Aucun log disponible</p>
          <p className="tw-text-sm">L'historique des actions apparaîtra ici.</p>
        </div>
      )}
    </div>
  );
};
